import ApplicationController from "./application_controller";

// Warnt Bewerber auf der Abschluss-Seite, solange eine Bewerbung vorbereitet, aber noch
// NICHT abgesendet ist (Apply-Status 'new'). Die View rendert den Controller nur dann,
// wenn Setting.feature_exit_popup aktiv ist UND eine unabgesendete Bewerbung existiert.
//
// - Klicks auf Links, die aus der Bewerbung herausfuehren (Logo, Menue, Abmelden, Footer,
//   externe Seiten), werden abgefangen und zeigen das eigene Modal.
// - Links innerhalb der Bewerbung (Schritte, Zurueck, eigene Bewerberdaten) warnen nicht.
// - Exit-Intent (optional, Setting.feature_exit_popup_exit_intent, nur Desktop): verlaesst
//   der Mauszeiger das Fenster am oberen Rand (Richtung Tab, Schliessen, Adresszeile), zeigt
//   sich das Modal einmal pro Seitenaufruf. Waehrend und kurz nach Drag & Drop (z. B. PDF in
//   den Upload ziehen) und ohne Fensterfokus (Datei-Dialog offen) loest es nicht aus.
// - Tab schliessen / Adresszeile laufen ueber beforeunload; dort zeigen Browser aus
//   Sicherheitsgruenden nur ihren eigenen Standarddialog, der sich nicht ersetzen laesst.
//   Neu laden per Tastatur (F5, Strg/Cmd+R) laeuft ohne Dialog, weil dabei nichts verloren geht.
// - Nur "Bewerbung absenden" schaltet die Warnung ab; andere Formulare (z. B. Upload)
//   unterdruecken lediglich den Browser-Dialog fuer ihren eigenen Seitenwechsel.

// Pfade, die zum Bewerbungsablauf gehoeren (optional mit Locale-Praefix).
const IN_FLOW_PATH = /^(\/[a-z]{2})?\/(apply_steps|applicants\/[^/]+)(\/|$)/;
const SUBMIT_APPLY_PATH = /\/apply_steps\/submit_apply\//;
const DESKTOP_POINTER = "(hover: hover) and (pointer: fine)";
const DRAG_COOLDOWN_MS = 3000;

export default class extends ApplicationController {
  static targets = ["dialog", "message"];
  static values = { exitIntent: Boolean };

  connect() {
    this.armed = true;
    this.submitting = false;
    this.reloading = false;
    this.pendingLink = null;
    this.exitIntentShown = false;
    this.openedByExitIntent = false;
    this.dragging = false;
    this.lastDragAt = 0;

    this.onClick = this.interceptClick.bind(this);
    this.onBeforeUnload = this.beforeUnload.bind(this);
    this.onSubmit = this.submitStarted.bind(this);
    this.onSubmitEnd = this.submitEnded.bind(this);
    this.onKeydown = this.keydown.bind(this);
    this.onMouseOut = this.mouseOut.bind(this);
    this.onDragActive = this.dragActive.bind(this);
    this.onDragDone = this.dragDone.bind(this);

    // Capture-Phase, damit der Klick vor Turbo und rails-ujs (data-method) ankommt.
    document.addEventListener("click", this.onClick, true);
    window.addEventListener("beforeunload", this.onBeforeUnload);
    document.addEventListener("submit", this.onSubmit, true);
    document.addEventListener("turbo:submit-end", this.onSubmitEnd);
    document.addEventListener("ajax:complete", this.onSubmitEnd);
    document.addEventListener("keydown", this.onKeydown);

    if (this.exitIntentEnabled()) {
      document.addEventListener("mouseout", this.onMouseOut);
      ["dragstart", "dragenter", "dragover"].forEach((type) => document.addEventListener(type, this.onDragActive, true));
      ["drop", "dragend", "dragleave"].forEach((type) => document.addEventListener(type, this.onDragDone, true));
    }
  }

  disconnect() {
    document.removeEventListener("click", this.onClick, true);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    document.removeEventListener("submit", this.onSubmit, true);
    document.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    document.removeEventListener("ajax:complete", this.onSubmitEnd);
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("mouseout", this.onMouseOut);
    ["dragstart", "dragenter", "dragover"].forEach((type) => document.removeEventListener(type, this.onDragActive, true));
    ["drop", "dragend", "dragleave"].forEach((type) => document.removeEventListener(type, this.onDragDone, true));
  }

  interceptClick(event) {
    if (!this.armed || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest("a[href]");
    if (!link || this.dialogTarget.contains(link) || !this.leavesApplication(link)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.pendingLink = link;
    this.openedByExitIntent = false;
    this.open();
  }

  leavesApplication(link) {
    const href = link.getAttribute("href");
    if (!href || href.startsWith("#") || /^(javascript|mailto|tel):/i.test(href)) return false;
    // Neuer Tab/Download laesst die Bewerbung offen.
    if (link.target === "_blank" || link.hasAttribute("download")) return false;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return true;
    return !IN_FLOW_PATH.test(url.pathname);
  }

  exitIntentEnabled() {
    return this.exitIntentValue && window.matchMedia(DESKTOP_POINTER).matches;
  }

  mouseOut(event) {
    // Nur wenn der Zeiger das Fenster ganz verlaesst (kein Ziel-Element) und zwar nach oben.
    if (event.relatedTarget || event.clientY > 0) return;
    if (!this.armed || this.exitIntentShown || this.isOpen()) return;
    if (this.dragging || Date.now() - this.lastDragAt < DRAG_COOLDOWN_MS) return;
    if (!document.hasFocus()) return;

    this.exitIntentShown = true;
    this.pendingLink = null;
    this.openedByExitIntent = true;
    this.open();
  }

  dragActive() {
    this.dragging = true;
    this.lastDragAt = Date.now();
  }

  dragDone() {
    this.dragging = false;
    this.lastDragAt = Date.now();
  }

  isOpen() {
    return !this.dialogTarget.classList.contains("hidden");
  }

  open() {
    this.dialogTarget.classList.remove("hidden");
    this.dialogTarget.querySelector("[data-autofocus]")?.focus();
  }

  close() {
    this.dialogTarget.classList.add("hidden");
  }

  stay() {
    const scrollToSubmit = this.openedByExitIntent;
    this.pendingLink = null;
    this.openedByExitIntent = false;
    this.close();
    if (scrollToSubmit) this.submitButton()?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  stayOnBackdrop(event) {
    if (event.target === event.currentTarget) this.stay();
  }

  leave() {
    const link = this.pendingLink;
    this.armed = false;
    this.openedByExitIntent = false;
    this.close();
    // Erneuter Klick, damit Turbo bzw. rails-ujs (z. B. Abmelden per DELETE) normal greifen.
    if (link) link.click();
  }

  submitButton() {
    const form = Array.from(document.querySelectorAll("form")).find((f) =>
      SUBMIT_APPLY_PATH.test(f.getAttribute("action") || "")
    );
    return form?.querySelector("button, [type=submit]");
  }

  keydown(event) {
    if (event.key === "Escape" && this.isOpen()) {
      this.stay();
      return;
    }
    const reloadShortcut = event.key === "F5" || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r");
    if (reloadShortcut) this.reloading = true;
  }

  beforeUnload(event) {
    if (!this.armed || this.submitting || this.reloading) return;
    // Browser ignorieren den Text und zeigen ihren Standarddialog; returnValue muss gesetzt sein.
    event.preventDefault();
    event.returnValue = this.messageTarget.textContent.trim();
    return event.returnValue;
  }

  submitStarted(event) {
    const form = event.target;
    if (SUBMIT_APPLY_PATH.test(form.getAttribute("action") || "")) {
      this.armed = false;
    } else {
      this.submitting = true;
    }
  }

  submitEnded() {
    this.submitting = false;
  }
}
